import { useContext, useMemo } from 'react';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import { Sparkle, X } from '@phosphor-icons/react';
import { PromptLoadingReason, SVG_SIZE_M } from 'src/constants';
import { useSession, useBreakpoint } from 'src/hooks';
import log from 'loglevel';
import { getBaseHeaders } from 'src/store/services/config';
import { bffTasksBaseUrl } from 'src/store/constants';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import styles from './PromptButton.module.scss';
import { Button } from 'src/v2/commonComponents/Button';
import { extractQuickSkillCommand } from 'src/utils';
import { AppRoutes, QuickSkillCommand } from 'src/types';
import { SpinnerSimple } from 'src/v2/commonComponents/SpinnerSimple';
import AuthContext from 'src/contexts/AuthContext';

type PromptButtonProps = {
  onClose?: () => void;
  undoTriggerRef: React.MutableRefObject<boolean>;
  setImprovedPrompt: (value: string) => void;
  handleUndo: () => void;
  isLoading: boolean;
};

const textDecoder = new TextDecoder();

export const PromptButton = ({
  onClose,
  undoTriggerRef,
  isLoading,
  setImprovedPrompt,
  handleUndo,
}: PromptButtonProps) => {
  const { threadInputBoxRef } = useContext(ForwardRefContext);
  const { appUser } = useSession();
  const { isMobileOrTablet } = useBreakpoint();
  const {
    threadInputBoxValue,
    setTemporaryInputValue,
    setPromptLoading,
    setPromptModificationReason,
    temporaryInputValue,
    setThreadInputBoxValue,
  } = useContext(ThreadInputBoxContext);
  const { isGuestAccess } = useContext(AuthContext);

  const handleReturnInputToFocus = () => {
    if (threadInputBoxRef?.current) {
      threadInputBoxRef.current.focus();
    }
  };

  const addCommandToValue = ({
    value,
    command,
  }: {
    value: string;
    command?: QuickSkillCommand;
  }) => {
    return command ? `${command} ${value}` : value;
  };

  const updatePrompt = async ({
    response,
    stream,
    command,
  }: {
    response: Response;
    stream: boolean;
    command?: QuickSkillCommand;
  }) => {
    let value = '';
    if (stream) {
      if (undoTriggerRef.current) {
        undoTriggerRef.current = false;
        return;
      }
      if (response.body) {
        if (response.body) {
          const reader = response.body.getReader();

          while (true) {
            const { done, value: chunk } = await reader.read();

            if (done) {
              break;
            }

            value += textDecoder.decode(chunk, { stream: true });

            if (undoTriggerRef.current) {
              undoTriggerRef.current = false;
              return;
            }

            setThreadInputBoxValue(addCommandToValue({ value, command }));
          }

          setImprovedPrompt(addCommandToValue({ value, command }));
        }
      }
    } else {
      const result = await response.json();
      setThreadInputBoxValue(
        addCommandToValue({ value: result.improved_prompt, command }),
      );
      setImprovedPrompt(addCommandToValue({ value, command }));
    }
  };

  const handleSendPrompt = async () => {
    if (isGuestAccess) {
      window.location.href = AppRoutes.LOGIN;
      return;
    }

    try {
      onClose?.();
      setPromptLoading(true);
      setPromptModificationReason(PromptLoadingReason.IMPROVING);
      setTemporaryInputValue(threadInputBoxValue);
      setThreadInputBoxValue('');

      const { command, cleanedInput } =
        extractQuickSkillCommand(threadInputBoxValue);

      // TODO(Inna): it's a temporary workaround to handle response as a streaming (needs to investigate RTK ability)
      await fetch(
        `${bffTasksBaseUrl}/users/${appUser.user_id}/prompts/improve`,
        {
          method: 'POST',
          headers: await getBaseHeaders(
            new Headers({
              'Content-Type': 'application/json',
            }),
          ),
          body: JSON.stringify({ prompt: cleanedInput, stream: true }),
        },
      )
        .then((response) =>
          updatePrompt({
            response,
            stream: true,
            command,
          }),
        )
        .catch((err) => log.error(err));
      setPromptLoading(false);
    } catch (e) {
      if (undoTriggerRef.current) {
        undoTriggerRef.current = false;
      }
      setPromptLoading(false);
      setThreadInputBoxValue(temporaryInputValue);
      setTemporaryInputValue('');
      log.error(e);
    }
    handleReturnInputToFocus();
  };

  const buttonTitle = useMemo(
    () => (isLoading ? 'Cancel' : 'Improve prompt'),
    [isLoading],
  );

  if (isMobileOrTablet) {
    return isLoading ? (
      <Button color="tertiary" onClick={handleUndo} shape="round">
        <X size={SVG_SIZE_M} />
        <SpinnerSimple className={styles.spinner} />
      </Button>
    ) : (
      <Button
        color="secondary-outline"
        onClick={handleSendPrompt}
        disabled={!threadInputBoxValue.trim()}
        shape="round"
      >
        <Sparkle size={SVG_SIZE_M} />
      </Button>
    );
  }

  return (
    <Button
      fullWidth
      color={isLoading ? 'tertiary-outline' : 'secondary-outline'}
      onClick={isLoading ? handleUndo : handleSendPrompt}
      disabled={!isLoading && !threadInputBoxValue.trim()}
    >
      {isLoading ? (
        <SpinnerSimple size={SVG_SIZE_M} />
      ) : (
        <Sparkle size={SVG_SIZE_M} />
      )}

      {buttonTitle}
    </Button>
  );
};
